import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { createPortal } from 'react-dom';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';

type Option = {
  label: string;
  value: string;
};

type Props = {
  title: string;
  allLabel?: string;
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
};

/**
 * Reusable checkbox dropdown filter.
 * Uses useDropdownPosition hook for shared positioning/click-outside logic.
 */
const CheckboxDropdown: React.FC<Props> = ({ title, allLabel, options, selected, onChange }) => {
  const { open, setOpen, buttonRef, popoverRef, position } = useDropdownPosition();

  const label = selected.length ? `${selected.length} ${title}` : allLabel || `All ${title}`;

  const toggle = (value: string, checked: boolean) => {
    if (checked) {
      onChange([...selected, value]);
    } else {
      onChange(selected.filter(v => v !== value));
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex h-9 items-center gap-2 rounded-lg border bg-white text-sm px-3 min-w-[140px] justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 hover:shadow-sm transition ${
          selected.length ? 'border-primary-400/70 text-gray-900' : 'border-gray-300 text-gray-700'
        }`}
      >
        <span className="truncate">{label}</span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="w-[220px] rounded-xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 p-3"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 9999,
            }}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                {title}
              </div>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2">
              {options.map(opt => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={e => toggle(opt.value, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CheckboxDropdown;
